/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useState, type PropsWithChildren } from 'react';
import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import type { MaybeChoreographedComponentProps } from '@jira/platform__choreographer-services/src/types.tsx';
import { shouldBeChoreographed } from '@jira/platform__choreographer-services/src/utils.tsx';
import { cssMap, jsx } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import DeprecatedSpotlight from '@atlaskit/onboarding/spotlight';
import { PopoverContent, type PopoverContentProps } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';
import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';
import {
	SpotlightSecondaryLink,
	type SpotlightSecondaryLinkProps,
} from '@atlaskit/spotlight/secondary-link';
import {
	type SpotlightPrimaryLinkProps,
	SpotlightPrimaryLink,
} from '@atlaskit/spotlight/primary-link';

import { Text } from '@atlaskit/primitives/compiled/text';
import type { Actions } from '@atlaskit/onboarding/types';
import { ChoreographerSpotlight } from './choreographer-spotlight.tsx';
import type { SpotlightProps } from './types.tsx';

import { actionsTranslation } from './translations/actionsTranslation.tsx';
import { headingTranslation } from './translations/headingTranslation.tsx';
import { childrenTranslation } from './translations/childrenTranslation.tsx';
import { dialogPlacementTranslation } from './translations/dialogPlacementTranslation.tsx';
import { targetTranslation } from './translations/targetTranslation.tsx';
import { ChoreographedComponent } from './ChoreographedComponent.tsx';

const styles = cssMap({
	image: {
		width: '100%',
	},
	// The spotlight popup renders inline inside the target element, so it
	// inherits text-related CSS properties (white-space, text-align, etc.) from ancestors.
	// Reset them to safe defaults to keep the popup readable regardless of where it's mounted.
	contentReset: {
		textAlign: 'initial',
		whiteSpace: 'initial',
		textTransform: 'initial',
		wordBreak: 'initial',
		overflowWrap: 'normal',
	},
});

const translations = {
	actions: actionsTranslation,
	heading: headingTranslation,
	children: childrenTranslation,
	dialogPlacement: dialogPlacementTranslation,
	target: targetTranslation,
};

export type Action = Actions[number] & { ctaType?: 'primary' | 'secondary'; href?: string };

export type JiraSpotlightProps = SpotlightProps &
	MaybeChoreographedComponentProps & {
		isMigrated?: boolean;
		offset?: PopoverContentProps['offset'];
		actions?: Action[];
		/**
		 * Use in useMountToHtmlElement; Whether to observe for mutations.
		 * - When true, enables MutationObserver to watch for dynamic elements.
		 * - When not provided, only performs a one-time query.
		 */
		shouldObserveRootForTarget?: boolean;
		/**
		 * Called when the spotlight is dismissed via the X button or clicking outside.
		 * Only fires in the migrated path (isMigrated=true).
		 */
		onDismiss?: () => void;
	};

const ContentStylesReset = ({ children }: PropsWithChildren) => {
	return (
		<div css={styles.contentReset} data-testid="spotlight.ui.content-styles-reset">
			{children}
		</div>
	);
};

export const JiraSpotlight = ({
	isMigrated = false,
	...props
}: JiraSpotlightProps): JSX.Element => {
	if (isMigrated) {
		return <Spotlight {...props} />;
	}

	if (shouldBeChoreographed(props)) {
		const { messageType, ...rest } = props;

		return <ChoreographerSpotlight {...rest} />;
	}

	const { messageType, messageId, onMessageDisposition, ...rest } = props;

	return <DeprecatedSpotlight {...rest} />;
};

const Spotlight = (props: JiraSpotlightProps) => {
	const placement = translations.dialogPlacement(props.dialogPlacement);
	const actions = translations.actions(props.actions);
	const heading = translations.heading(props.heading);
	const body = translations.children(props.children);
	const id = translations.target(props.target);

	const [isVisible, setIsVisible] = useState(true);

	const { onDismiss } = props;
	const dismiss = useCallback(() => {
		setIsVisible(false);
		onDismiss?.();
	}, [setIsVisible, onDismiss]);

	const renderInline = useMountToHtmlElement({
		selectors: id ? `#${id}` : undefined,
		as: 'inline',
		position: 'inside',
		observeRoot:
			props.shouldObserveRootForTarget && typeof globalThis.document !== 'undefined'
				? globalThis.document.body
				: undefined,
	});

	const { messageType, messageId } = props;

	return renderInline(
		<ChoreographedComponent messageId={messageId} messageType={messageType}>
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent
					offset={props.offset}
					dismiss={dismiss}
					placement={placement}
					isVisible={isVisible}
				>
					<ContentStylesReset>
						{/* eslint-disable-next-line jira/choreographer/spotlight-card -- This is the package that defines ChoreographedComponent */}
						<SpotlightCard>
							<SpotlightHeader>
								<SpotlightHeadline>{heading}</SpotlightHeadline>
								<SpotlightControls>
									<SpotlightDismissControl />
								</SpotlightControls>
							</SpotlightHeader>
							{props.image && (
								<SpotlightMedia>
									<Image css={styles.image} src={props.image} alt="" />
								</SpotlightMedia>
							)}
							<SpotlightBody>
								<Text>{body}</Text>
							</SpotlightBody>
							<SpotlightFooter>
								{props.actionsBeforeElement && (
									<SpotlightStepCount>{props.actionsBeforeElement}</SpotlightStepCount>
								)}
								<SpotlightActions>
									<>
										{actions?.secondary &&
											(actions.secondary.href !== undefined ? (
												<SpotlightSecondaryLink
													// oxlint-disable-next-line typescript/consistent-type-assertions
													{...(actions.secondary as SpotlightSecondaryLinkProps)}
												>
													{actions.secondary.text}
												</SpotlightSecondaryLink>
											) : (
												<SpotlightSecondaryAction {...actions.secondary}>
													{actions.secondary.text}
												</SpotlightSecondaryAction>
											))}
										{actions?.primary &&
											(actions.primary.href !== undefined ? (
												// oxlint-disable-next-line typescript/consistent-type-assertions
												<SpotlightPrimaryLink {...(actions.primary as SpotlightPrimaryLinkProps)}>
													{actions.primary.text}
												</SpotlightPrimaryLink>
											) : (
												<SpotlightPrimaryAction {...actions.primary}>
													{actions.primary.text}
												</SpotlightPrimaryAction>
											))}
									</>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					</ContentStylesReset>
				</PopoverContent>
			</PopoverProvider>
		</ChoreographedComponent>,
	);
};
